/* eslint no-fallthrough: 0, default-case: 0 */
import * as dateMath from 'date-arithmetic';
import extend from 'lodash/extend';
import localizer from '../localizer';

const MILLI = {
  milliseconds: 1,
  seconds: 1000,
  minutes: 1000 * 60,
  hours: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
};

// Add a safe date function wrapper
const safeDateCall = (fn, defaultValue = null) => {
  return (...args) => {
    const hasInvalidDate = args.some(arg => {
      // allow non-Date args like 'day', 'week', etc.
      if (arg == null) return true;
      if (arg instanceof Date) return isNaN(arg.getTime());
      return false; // ignore non-Date values
    });

    if (hasInvalidDate) {
      console.warn('Invalid date passed to date function:', args);
      return defaultValue;
    }

    try {
      return fn(...args);
    } catch (error) {
      console.error('Error in date function:', error);
      return defaultValue;
    }
  };
};

const dates = extend({}, dateMath, {
  startOf: safeDateCall(dateMath.startOf),
  endOf: safeDateCall(dateMath.endOf),
  add: safeDateCall(dateMath.add),
  eq: safeDateCall(dateMath.eq, false),
  neq: safeDateCall(dateMath.neq, false),
  gt: safeDateCall(dateMath.gt, false),
  gte: safeDateCall(dateMath.gte, false),
  lt: safeDateCall(dateMath.lt, false),
  lte: safeDateCall(dateMath.lte, false),
  inRange: safeDateCall(dateMath.inRange, false),
  min: safeDateCall(dateMath.min),
  max: safeDateCall(dateMath.max),
  hours: safeDateCall(dateMath.hours, 0),
  minutes: safeDateCall(dateMath.minutes, 0),
  seconds: safeDateCall(dateMath.seconds, 0),
  milliseconds: safeDateCall(dateMath.milliseconds, 0),
  month: safeDateCall(dateMath.month, 0),

  monthsInYear(year) {
    const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    const date = new Date(year, 0, 1);

    return months.map(i => dates.month(date, i));
  },

  firstVisibleDay(date, culture) {
    if (!date) return null;
    const firstOfMonth = dates.startOf(date, 'month');
    return dates.startOf(firstOfMonth, 'week', localizer.startOfWeek(culture));
  },

  lastVisibleDay(date, culture) {
    if (!date) return null;
    const endOfMonth = dates.endOf(date, 'month');
    return dates.endOf(endOfMonth, 'week', localizer.startOfWeek(culture));
  },

  visibleDays(date, culture) {
    if (!date) return [];
    let current = dates.firstVisibleDay(date, culture);
    const last = dates.lastVisibleDay(date, culture);
    const days = [];

    while (current && last && dates.lte(current, last, 'day')) {
      days.push(current);
      current = dates.add(current, 1, 'day');
    }

    return days;
  },

  ceil(date, unit) {
    if (!date) return null;
    const floor = dates.startOf(date, unit);
    return dates.eq(floor, date) ? floor : dates.add(floor, 1, unit);
  },

  range(start, end, unit = 'day') {
    if (!start || !end) return [];
    let current = start;
    const days = [];

    while (dates.lte(current, end, unit)) {
      days.push(current);
      current = dates.add(current, 1, unit);
    }

    return days;
  },

  merge(_date, _time) {
    let date = _date;
    let time = _time;

    if (time == null && date == null) {
      return null;
    }

    if (time == null) time = new Date();
    if (date == null) date = new Date();

    // Check if dates are valid
    if (isNaN(date.getTime()) || isNaN(time.getTime())) {
      return new Date();
    }

    date = dates.startOf(date, 'day');
    date = dates.hours(date, dates.hours(time));
    date = dates.minutes(date, dates.minutes(time));
    date = dates.seconds(date, dates.seconds(time));
    return dates.milliseconds(date, dates.milliseconds(time));
  },

  sameMonth(dateA, dateB) {
    if (!dateA || !dateB) return false;
    return dates.eq(dateA, dateB, 'month');
  },

  eqTime(dateA, dateB) {
    if (!dateA || !dateB) return false;
    return dates.hours(dateA) === dates.hours(dateB) &&
      dates.minutes(dateA) === dates.minutes(dateB) &&
      dates.seconds(dateA) === dates.seconds(dateB);
  },

  isJustDate(date) {
    if (!date) return false;
    return (
      dates.hours(date) === 0 &&
      dates.minutes(date) === 0 &&
      dates.seconds(date) === 0 &&
      dates.milliseconds(date) === 0
    );
  },

  duration(start, end, _unit, firstOfWeek) {
    if (!start || !end) return 0;
    let unit = _unit;
    if (unit === 'day') unit = 'date';
    return Math.abs(dates[unit](start, undefined, firstOfWeek) - dates[unit](end, undefined, firstOfWeek));
  },

  diff(dateA, dateB, unit) {
    if (!dateA || !dateB) {
      console.warn('dates.diff: Missing date parameters', { dateA, dateB });
      return 0;
    }
    
    // Check for invalid dates
    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
      return 0;
    }
    
    if (!unit) {
      return Math.abs(+dateA - +dateB);
    }

    return Math.round(Math.abs(
      (+dates.startOf(dateA, unit) / MILLI[unit]) - (+dates.startOf(dateB, unit) / MILLI[unit])
    ));
  },

  total(date, unit) {
    if (!date || isNaN(date.getTime())) return 0;
    const ms = date.getTime();
    let div = 1;

    switch (unit) {
      case 'week':
        div *= 7;
      case 'day':
        div *= 24;
      case 'hours':
        div *= 60;
      case 'minutes':
        div *= 60;
      case 'seconds':
        div *= 1000;
    }

    return ms / div;
  },

  week(date) {
    if (!date || isNaN(date.getTime())) return 0;
    const d = new Date(date);
    d.setHours(0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    return Math.ceil((((d - new Date(d.getFullYear(), 0, 1)) / 8.64e7) + 1) / 7);
  },

  today() {
    return dates.startOf(new Date(), 'day');
  },

  yesterday() {
    return dates.add(dates.startOf(new Date(), 'day'), -1, 'day');
  },

  tomorrow() {
    return dates.add(dates.startOf(new Date(), 'day'), 1, 'day');
  },
});

export default dates;